import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Navbar from './components/Navbar';
import HomePage from './pages/HomePage';
import TrackOrderPage from './pages/TrackOrderPage';
import ContactPage from './pages/ContactPage';
import AdminDashboard from './pages/AdminDashboard';
import FloatingContactButton from './components/FloatingContactButton';
import CallModal from './components/CallModal';
import OrderModal from './components/OrderModal';
import OperatorPlansModal from './components/OperatorPlansModal';
import PopupModal from './components/PopupModal';
import { planService } from './services/planService';
import { Facebook, Instagram, Linkedin, Youtube } from 'lucide-react';


function MainApp() {
  const [currentPage, setCurrentPage] = useState('home');
  const [plans, setPlans] = useState([]);
  const [callModalOpen, setCallModalOpen] = useState(false);
  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [plansModalOpen, setPlansModalOpen] = useState(false);
  const [selectedOperator, setSelectedOperator] = useState(null);
  const [selectedPlanType, setSelectedPlanType] = useState('prepaid');
  const [initialOrderType, setInitialOrderType] = useState('New SIM');
  const [orderConfirmation, setOrderConfirmation] = useState(null);

  useEffect(() => {
    loadPlans();
  }, []);

  async function loadPlans() {
    try {
      const data = await planService.getActivePlans();
      setPlans(data || []);
    } catch (err) {
      console.error(err);
    }
  }

  const handleOrderNow = (plan = null) => {
    setSelectedPlan(plan);
    setInitialOrderType('New SIM');
    setOrderModalOpen(true);
  };

  const handleOpenPlans = (operator, planType = selectedPlanType) => {
    setSelectedOperator(operator);
    setSelectedPlanType(planType);
    setInitialOrderType('New SIM');
    setPlansModalOpen(true);
  };

  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan);
    setOrderModalOpen(true);
  };

  const handlePortNumber = () => {
    setSelectedOperator('Top SIM Cards');
    setSelectedPlanType('all');
    setInitialOrderType('Port-in');
    setPlansModalOpen(true);
  };

  const handleOrderSuccess = (order) => {
    setOrderConfirmation({
      title: 'Order placed successfully',
      message: [
        `Order number: ${order.order_number || 'Not available'}`,
        `Order ID: ${order.id || 'Not available'}`,
        `Name: ${order.customer_name || 'Not available'}`,
        `Operator: ${order.operator || 'Not available'}`,
        `Plan: ${order.plan_name || 'Not available'}`,
        `Amount: ₹${order.plan_price || '0'}`,
        `Status: ${order.status || 'Pending'}`
      ].join('\n')
    });
  };

  const handleNavbarOrderNow = () => {
    setCurrentPage('home');
    setTimeout(() => {
      document.getElementById('operators')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  return (
    <div className="min-h-screen flex flex-col bg-white text-slate-900 relative">
      <Navbar
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        onOrderNow={handleNavbarOrderNow}
      />

      <main className="flex-1">
        {['home', 'operators', 'how-it-works'].includes(currentPage) && (
          <HomePage
            initialSection={currentPage === 'operators' ? 'our-operators-section' : currentPage === 'how-it-works' ? 'how-it-works-section' : null}
            onOpenPlans={handleOpenPlans}
            onPortNumber={handlePortNumber}
            onCallClick={() => setCallModalOpen(true)}
          />
        )}
        {currentPage === 'track' && <TrackOrderPage />}
        {currentPage === 'contact' && <ContactPage />}
      </main>

      {/* Floating Call/WhatsApp/Email Button */}
      <FloatingContactButton />

      <OperatorPlansModal
        isOpen={plansModalOpen}
        operator={selectedOperator}
        initialPlanType={selectedPlanType}
        plans={plans}
        onClose={() => setPlansModalOpen(false)}
        onSelectPlan={handleSelectPlan}
      />

      {/* Custom Call Modal */}
      <CallModal isOpen={callModalOpen} onClose={() => setCallModalOpen(false)} />

      {/* Direct Order Now Modal */}
      <OrderModal
        isOpen={orderModalOpen}
        selectedPlan={selectedPlan}
        initialOrderType={initialOrderType}
        onClose={() => setOrderModalOpen(false)}
        onSuccess={handleOrderSuccess}
      />

      <PopupModal
        isOpen={!!orderConfirmation}
        title={orderConfirmation?.title || ''}
        message={orderConfirmation?.message || ''}
        onClose={() => setOrderConfirmation(null)}
      />

      {/* Footer */}
      <footer className="bg-slate-950 text-white pt-14 pb-8 border-t border-slate-900 mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10 pb-12 border-b border-slate-800 text-xs">
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-orange-600 flex items-center justify-center text-white font-bold">A1</div>
                <span className="text-lg font-bold">A1 Sims</span>
              </div>
              <p className="text-slate-400 leading-relaxed">
                India's fastest SIM card doorstep delivery service. We bring connectivity to you.
              </p>
              <div className="text-slate-400 font-semibold">a1sims.com</div>
              <div className="flex items-center gap-2 pt-1">
                <a href="https://www.instagram.com/a1_sims?stkn=MTBud3FheGZpNnV6Yg==" target="_blank" rel="noreferrer" aria-label="A1 Sims on Instagram" className="rounded-full border border-slate-700 p-2 text-slate-400 hover:border-orange-400 hover:text-orange-400"><Instagram size={15} /></a>
                <a href="https://www.facebook.com/share/1CaNNGSyPP/" target="_blank" rel="noreferrer" aria-label="A1 Sims on Facebook" className="rounded-full border border-slate-700 p-2 text-slate-400 hover:border-orange-400 hover:text-orange-400"><Facebook size={15} /></a>
                <a href="https://x.com/VivekKumar8368" target="_blank" rel="noreferrer" aria-label="A1 Sims on X" className="rounded-full border border-slate-700 p-2 text-xs font-bold text-slate-400 hover:border-orange-400 hover:text-orange-400">X</a>
                <a href="https://www.linkedin.com/in/a1sims-delhi-ncr-sim-delivery-bb73782b8?utm_source=share_via&utm_content=profile&utm_medium=member_android" target="_blank" rel="noreferrer" aria-label="A1 Sims on LinkedIn" className="rounded-full border border-slate-700 p-2 text-slate-400 hover:border-orange-400 hover:text-orange-400"><Linkedin size={15} /></a>
                <a href="https://youtube.com/@a1simsdeliverydelhincr?si=qRSERuS2iq4BncJm" target="_blank" rel="noreferrer" aria-label="A1 Sims on YouTube" className="rounded-full border border-slate-700 p-2 text-slate-400 hover:border-orange-400 hover:text-orange-400"><Youtube size={15} /></a>
              </div>
              {/* <a href="/admin-portal" className="inline-flex items-center rounded-xl border border-slate-700 px-3 py-2 text-xs font-bold text-slate-300 transition hover:border-orange-400 hover:bg-orange-500/10 hover:text-orange-300">
                Admin Portal
              </a> */}
            </div>

            <div className="space-y-2">
              <h4 className="font-bold text-white uppercase text-xs tracking-wider mb-3">Services</h4>
              <div onClick={handleOrderNow} className="text-slate-400 hover:text-white cursor-pointer">New SIM Activation</div>
              <div onClick={handleOrderNow} className="text-slate-400 hover:text-white cursor-pointer">SIM Port (MNP)</div>
              <div onClick={handleOrderNow} className="text-slate-400 hover:text-white cursor-pointer">Plan Upgrade</div>
              <div onClick={handleOrderNow} className="text-slate-400 hover:text-white cursor-pointer">SIM Replacement</div>
            </div>

            <div className="space-y-2.5">
              <h4 className="font-bold text-white uppercase text-xs tracking-wider mb-3">Contact</h4>
              <div className="text-slate-300 font-semibold">Tiwari</div>
              <button onClick={() => setCallModalOpen(true)} className="text-slate-400 hover:text-white flex items-center gap-2">
                <span>📞</span> +91 92666 04406
              </button>
              <div className="text-slate-400 flex items-center gap-2">
                <span>✉️</span> support@a1sims.com
              </div>
              <div className="text-slate-400 flex items-center gap-2">
                <span>⏰</span> Available 10AM – 8PM daily
              </div>
              <div className="text-slate-400 flex items-center gap-2">
                <span>📍</span> Delhi, Noida, Ghaziabad, Greater Noida, Faridabad
              </div>
            </div>

            <div className="space-y-3">
              <h4 className="font-bold text-white uppercase text-xs tracking-wider mb-3">Quality Commitment</h4>
              <p className="text-slate-400 leading-relaxed">
                Govt. approved eKYC activation right at your home with zero paperwork.
              </p>
              <div className="flex flex-col items-start gap-2 pt-1">
                <h3 className="text-slate-300 decoration-slate-600 hover:text-orange-400">GST Number : 09BZTPV4875R1ZN</h3>
                <h3 className="text-slate-300 decoration-slate-600  hover:text-orange-400">UDYAM Registration : UDYAM-UP-29-0244089</h3>
              </div>
            </div>
          </div>

          <div className="pt-6 text-center text-[11px] text-slate-500">
            <p>© 2026 A1 Sims. All rights reserved. SIM card delivery service in Delhi NCR. a1sims.com</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default function App() {
  return (
    <Router>
      <Routes>
        <Route path="/admin-portal" element={<AdminDashboard />} />
        <Route path="*" element={<MainApp />} />
      </Routes>
    </Router>
  );
}